/**
 * ToolDetail (Round 1.3 + edit support).
 *
 * Renders inside `<Drawer>`. Shows the full MCP tool record with
 * editable governance metadata (data_classification, side_effect_level,
 * status, restricted_to_wg_ids). Edits go through `useUpdateTool`,
 * which emits an audit event and invalidates the catalog/tools queries.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { AlertTriangle, Check, Edit2, X } from 'lucide-react';
import type { McpTool } from '@sumcp/shared-types';

import { ApiError, useUpdateTool, useWorkgroups } from '@/api/index.js';
import { Drawer } from '../Drawer.js';
import { HelpCallout } from '../HelpCallout.js';
import { RequiredLegend } from '../Required.js';

const DATA_CLASSIFICATIONS = [
  'public',
  'internal',
  'confidential',
  'restricted',
  'regulated',
] as const;
const SIDE_EFFECT_LEVELS = ['read_only', 'write_low', 'write_high', 'destructive'] as const;
const STATUSES = ['draft', 'active', 'retired'] as const;

interface ToolDetailProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly tool: McpTool | null;
  /** Refresh callback (parent re-fetches tool list after a save). */
  readonly onPersisted?: () => void;
}

export function ToolDetail({ open, onClose, tool, onPersisted }: ToolDetailProps): JSX.Element {
  const update = useUpdateTool();
  const wgs = useWorkgroups({ limit: 500 });

  const [editing, setEditing] = useState(false);
  const [dataCls, setDataCls] = useState<string>('');
  const [side, setSide] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [restricted, setRestricted] = useState<string[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  // Hydrate form state from `tool` on open / tool change.
  useEffect(() => {
    if (!tool) return;
    setDataCls(tool.data_classification ?? '');
    setSide(tool.side_effect_level ?? '');
    setStatus(tool.status ?? '');
    setDisplayName(tool.display_name ?? '');
    setDescription(tool.description ?? '');
    setRestricted([...((tool.restricted_to_wg_ids ?? []) as readonly string[])]);
    setEditing(false);
    setActionError(null);
  }, [tool]);

  const wgList = useMemo(() => wgs.data?.items ?? [], [wgs.data]);

  if (!tool) {
    return (
      <Drawer open={open} onClose={onClose} eyebrow="Tool" title="Loading…">
        <p className="subtle">No tool selected.</p>
      </Drawer>
    );
  }

  async function onSave(): Promise<void> {
    if (!tool) return;
    setActionError(null);
    try {
      await update.mutateAsync({
        id: tool.id!,
        patch: {
          display_name: displayName || undefined,
          description: description || undefined,
          data_classification: dataCls || undefined,
          side_effect_level: side || undefined,
          status: status || undefined,
          restricted_to_wg_ids: restricted,
        },
      });
      setEditing(false);
      onPersisted?.();
    } catch (e) {
      setActionError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  function toggleWg(id: string): void {
    setRestricted((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Tool"
      title={tool.display_name ?? tool.name ?? '—'}
      footer={
        editing ? (
          <>
            <button type="button" onClick={() => setEditing(false)}>
              <X size={14} aria-hidden /> Cancel
            </button>
            <button
              type="button"
              className="primary"
              onClick={onSave}
              disabled={update.isPending || !dataCls || !side || !status}
            >
              <Check size={14} aria-hidden /> Save
            </button>
          </>
        ) : (
          <button type="button" className="primary" onClick={() => setEditing(true)}>
            <Edit2 size={14} aria-hidden /> Edit
          </button>
        )
      }
    >
      <div className="drawer-stack">
        <HelpCallout
          title="What this drawer does"
          whatHappens={
            <>
              Edit the governance metadata for this tool. Saving emits a <code>tool.update</code>{' '}
              audit event. <strong>Restricted-to-WGs</strong> hides the tool from any user not in
              the listed workgroups, even if they're entitled to the parent server.
            </>
          }
          youNeed={[
            <>Pick the right data-classification and side-effect tier — these drive policy.</>,
            <>
              Restrict the tool only when the data it returns is more sensitive than the rest of the
              server's tools.
            </>,
          ]}
          afterwards={[
            <>Catalog and matrix views refresh within seconds.</>,
            <>End users not in the allowed WGs see the tool disappear from their view.</>,
          ]}
        />

        {editing && <RequiredLegend />}

        <KV label="Name" value={<code>{tool.name}</code>} />

        <KV
          label="Display name"
          value={
            editing ? (
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                style={inputStyle}
              />
            ) : (
              (tool.display_name ?? tool.name)
            )
          }
        />

        <KV
          label="Description"
          value={
            editing ? (
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            ) : (
              (tool.description ?? '—')
            )
          }
        />

        <KV label="Server ID" value={<code>{tool.server_id}</code>} />

        <KV
          label={editing ? 'Data classification *' : 'Data classification'}
          value={
            editing ? (
              <select
                value={dataCls}
                onChange={(e) => setDataCls(e.target.value)}
                style={inputStyle}
                required
              >
                <option value="">— pick —</option>
                {DATA_CLASSIFICATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            ) : (
              <strong>{tool.data_classification ?? '—'}</strong>
            )
          }
        />

        <KV
          label={editing ? 'Side effect *' : 'Side effect'}
          value={
            editing ? (
              <select
                value={side}
                onChange={(e) => setSide(e.target.value)}
                style={inputStyle}
                required
              >
                <option value="">— pick —</option>
                {SIDE_EFFECT_LEVELS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              (tool.side_effect_level ?? '—')
            )
          }
        />

        <KV
          label={editing ? 'Status *' : 'Status'}
          value={
            editing ? (
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={inputStyle}
                required
              >
                <option value="">— pick —</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              (tool.status ?? '—')
            )
          }
        />

        {tool.version_hash && (
          <KV label="Version hash" value={<code>{tool.version_hash.slice(0, 16)}…</code>} />
        )}

        <KV
          label="Restricted to WGs"
          value={
            editing ? (
              <div
                style={{
                  display: 'grid',
                  gap: 4,
                  maxHeight: 180,
                  overflow: 'auto',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                {wgList.length === 0 ? (
                  <span className="subtle">No workgroups available.</span>
                ) : (
                  wgList.map((w) => (
                    <label
                      key={w.id}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                    >
                      <input
                        type="checkbox"
                        checked={restricted.includes(w.id)}
                        onChange={() => toggleWg(w.id)}
                      />
                      <code>{w.id}</code>
                      <span className="subtle">— {w.display_name}</span>
                    </label>
                  ))
                )}
                <p className="subtle" style={{ fontSize: 12, margin: '6px 0 0' }}>
                  Leave all unchecked to inherit server-level visibility.
                </p>
              </div>
            ) : restricted.length > 0 ? (
              restricted.map((id) => <code key={id}>{id} </code>)
            ) : (
              <span className="subtle">(none — visible per server-level policy)</span>
            )
          }
        />

        {actionError && (
          <p style={{ color: '#c0392b', fontSize: 14 }} role="alert">
            <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {actionError}
          </p>
        )}

        {tool.input_schema && (
          <div>
            <h3 style={{ margin: '14px 0 6px', fontSize: 15 }}>Input schema</h3>
            <pre
              style={{
                margin: 0,
                padding: 12,
                background: 'rgba(0, 0, 0, 0.06)',
                borderRadius: 8,
                fontSize: 13,
                fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
                overflow: 'auto',
                maxHeight: 220,
              }}
            >
              {JSON.stringify(tool.input_schema, null, 2)}
            </pre>
          </div>
        )}

        {tool.output_schema && (
          <div>
            <h3 style={{ margin: '14px 0 6px', fontSize: 15 }}>Output schema</h3>
            <pre
              style={{
                margin: 0,
                padding: 12,
                background: 'rgba(0, 0, 0, 0.06)',
                borderRadius: 8,
                fontSize: 13,
                fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
                overflow: 'auto',
                maxHeight: 220,
              }}
            >
              {JSON.stringify(tool.output_schema, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Drawer>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, max-content) 1fr',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <span className="subtle" style={{ fontSize: 13 }}>
        {label}
      </span>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  fontSize: 13,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};
